import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  Fade
} from '@mui/material';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEmpresa } from '../../context/EmpresaContext';

/**
 * Componente de tarjeta de orden para el tablero Kanban
 * @param {object} orden - Datos de la orden
 * @param {object} estado - Estado actual de la orden
 * @param {function} onView - Callback al ver detalles
 * @param {function} onUpdate - Callback al actualizar estado
 */
const OrdenCard = ({ orden, estado, onView, onUpdate }) => {
  const navigate = useNavigate();
  const { simboloMoneda } = useEmpresa();
  const [expanded, setExpanded] = useState(false);

  const formatCurrency = (amount) => {
    const formatted = parseFloat(amount || 0).toLocaleString('es-PE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${simboloMoneda} ${formatted}`;
  };

  const formatTime = (fecha) => {
    return new Date(fecha).toLocaleTimeString('es-PE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calcularTiempoTranscurrido = (fecha) => {
    const ahora = new Date();
    const fechaOrden = new Date(fecha);
    const diffMs = ahora - fechaOrden;
    const diffHoras = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHoras > 0) {
      return `${diffHoras}h ${diffMinutos}m`;
    }
    return `${diffMinutos}m`;
  };

  return (
    <Fade in={true} timeout={300}>
      <Card sx={{
        mb: 2,
        borderRadius: 3,
        borderLeft: `6px solid ${estado.color}`,
        boxShadow: `0 4px 20px ${estado.color}20`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: `0 12px 35px ${estado.color}30`
        },
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
      }}>
        <CardContent sx={{ p: 2 }}>
          {/* Header con botones de acción */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{
                fontWeight: 'bold',
                color: '#1A237E',
                textShadow: '0 1px 2px rgba(26, 35, 126, 0.1)'
              }}>
                OS #{orden.id_orden_servicio}
              </Typography>
              <Chip
                label={formatTime(orden.fecha_orden)}
                size="small"
                sx={{
                  background: `linear-gradient(135deg, ${estado.color}20, ${estado.color}10)`,
                  color: estado.color,
                  fontWeight: 'bold',
                  mt: 0.5,
                  border: `1px solid ${estado.color}30`
                }}
              />
            </Box>

            {/* Botones de acción con colores TRACKEA */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1 }}>
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/home/ordenes/editar/${orden.id_orden_servicio}`)}
                  sx={{
                    background: 'linear-gradient(135deg, #1A237E, #3949AB)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0D47A1, #1976D2)',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Ver detalles">
                <IconButton
                  size="small"
                  onClick={() => onView(orden)}
                  sx={{
                    background: 'linear-gradient(135deg, #4FC3F7, #29B6F6)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #29B6F6, #03A9F4)',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Cambiar estado">
                <IconButton
                  size="small"
                  onClick={() => onUpdate(orden)}
                  sx={{
                    background: 'linear-gradient(135deg, #4CAF50, #66BB6A)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #388E3C, #4CAF50)',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Expandir detalles">
                <IconButton
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                  sx={{
                    bgcolor: 'rgba(26, 35, 126, 0.1)',
                    color: '#1A237E',
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(26, 35, 126, 0.2)',
                      transform: expanded ? 'rotate(180deg) scale(1.1)' : 'rotate(0deg) scale(1.1)'
                    }
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Información principal */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PersonIcon sx={{ color: '#4FC3F7', mr: 1, fontSize: '1rem' }} />
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#1A237E' }}>
              {orden.nombre_cliente} {orden.apellido_cliente}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LocationIcon sx={{ color: '#4FC3F7', mr: 1, fontSize: '1rem' }} />
            <Typography variant="body2" color="text.secondary">
              {orden.nombre_local || 'Local no especificado'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TimeIcon sx={{ color: '#4FC3F7', mr: 0.5, fontSize: '0.9rem' }} />
              <Typography variant="caption" color="text.secondary">
                Hace: {calcularTiempoTranscurrido(orden.fecha_orden)}
              </Typography>
            </Box>

            {orden.precio_total && (
              <Chip
                label={formatCurrency(orden.precio_total)}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #1A237E, #3949AB)',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
            )}
          </Box>

          {/* Información del trabajador */}
          {orden.trabajadores && orden.trabajadores.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar sx={{
                width: 24,
                height: 24,
                mr: 1,
                background: `linear-gradient(135deg, ${estado.color}, ${estado.color}CC)`,
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }}>
                T
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                {orden.trabajadores[0].nombre_trabajador} {orden.trabajadores[0].apellido_trabajador}
              </Typography>
            </Box>
          )}

          {/* Sección expandible con detalles */}
          <Collapse in={expanded} timeout="auto">
            <Divider sx={{ my: 2, borderColor: 'rgba(26, 35, 126, 0.1)' }} />

            {/* Detalles de Servicios */}
            {orden.detalles && orden.detalles.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#1A237E',
                  fontWeight: 'bold'
                }}>
                  <BuildIcon fontSize="small" sx={{ color: '#4FC3F7' }} />
                  Servicios Solicitados
                </Typography>
                <List dense disablePadding>
                  {orden.detalles.map((detalle, index) => (
                    <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                      <ListItemText
                        primary={detalle.nombre_servicio}
                        secondary={
                          <React.Fragment>
                            <Typography variant="caption" display="block">
                              Cantidad: {detalle.cantidad} | Precio: {formatCurrency(detalle.precio)}
                            </Typography>
                            {detalle.detalle_glosa && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {detalle.detalle_glosa}
                              </Typography>
                            )}
                          </React.Fragment>
                        }
                      />
                      <Chip
                        size="small"
                        label={detalle.estado_servicio}
                        sx={{
                          ml: 1,
                          backgroundColor: detalle.id_estado_servicio === 5 ? '#4CAF50' : '#FFA726',
                          color: 'white'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Personal Asignado completo */}
            {orden.trabajadores && orden.trabajadores.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#1A237E',
                  fontWeight: 'bold'
                }}>
                  <PersonIcon fontSize="small" sx={{ color: '#4FC3F7' }} />
                  Personal Asignado
                </Typography>
                <List dense disablePadding>
                  {orden.trabajadores.map((trabajador, index) => (
                    <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                      <ListItemText
                        primary={`${trabajador.nombre_trabajador} ${trabajador.apellido_trabajador}`}
                        secondary={
                          <React.Fragment>
                            <Typography variant="caption" display="block">
                              {trabajador.telefono_trabajador} | {trabajador.email_trabajador}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Comisión: {formatCurrency(trabajador.comision)}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                      <Chip
                        size="small"
                        label={trabajador.estado_trabajador}
                        sx={{
                          ml: 1,
                          backgroundColor: trabajador.id_estado_servicio === 5 ? '#4CAF50' : '#FFA726',
                          color: 'white'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Collapse>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default OrdenCard;
